<template>
    <div>
        <div class="d-flex align-center">
            <!-- swipe right: same action as the invoice button -->
            <v-fab-transition>
                <v-btn fab absolute left v-show="invoiceFab" dark small text color="orange darken-2">
                    <v-icon>mdi-receipt-text-outline</v-icon>
                </v-btn>
            </v-fab-transition>

            <!-- card -->
            <v-card
				outlined
				rounded="lg"
				width="100%"
				class="visit-card mx-auto"
				v-ripple="{class:'visit-ripple'}"
				v-touch.sync="{
				move: (e) => { touchMove(e) },
				end: (e) => { endOffset(e) }
				}"
				:style="'transform: translateX('+ touchSwipeOffset +'px)'"
				:disabled="disabled"
            >
                <v-card-text class="px-3 pt-3 pb-0" @click="onCardClick" style="position: relative">
                    <!-- header: name + customer type chip -->
                    <div class="d-flex justify-space-between align-center">
                        <span class="text-h6 font-weight-bold green--text text--darken-2">
                            {{ visit.entity.name }}
                        </span>
                        <div class="d-flex align-center flex-shrink-0">
                            <v-chip
                                v-if="isPending"
                                x-small
                                color="amber lighten-4"
                                text-color="amber darken-3"
                                class="font-weight-medium me-1"
                            >
                                <v-icon x-small left color="amber darken-3">mdi-cloud-sync-outline</v-icon>
                                {{ $T('sync-queue.pending') }}
                            </v-chip>
                            <v-chip
                                v-if="customerTypeName"
                                x-small
                                color="orange lighten-5"
                                text-color="orange darken-2"
                                class="font-weight-medium"
                            >
                                {{ customerTypeName }}
                            </v-chip>
                            <v-btn
                                v-if="useVisitOrderRequest && !isPending"
                                icon
                                x-small
                                class="ms-1"
                                @click.stop="fetchAndShowOrder"
                            >
                                <v-icon small color="primary">mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </div>
                    </div>

                    <!-- date -->
                    <div class="d-flex align-center grey--text text--darken-1 mb-2">
                        <span class="text-caption">{{ formattedDate }}</span>
                        <v-icon size="15" class="ms-1" color="grey darken-1">mdi-calendar-blank-outline</v-icon>
                    </div>

                    <!-- marketing inputs, in the same order as the add-visit marketing tab -->
                    <div v-if="visit.notes" class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.visit-notes') }}</span>
                        <span>{{ visit.notes }}</span>
                    </div>

                    <div class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.core-message') }}</span>
                        <span :class="{ 'grey--text': !visit.coreMessage }">{{ visit.coreMessage || $T('no-data') }}</span>
                    </div>

                    <div v-if="visit.nextVisitNotes" class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.notes') }}</span>
                        <span>{{ visit.nextVisitNotes }}</span>
                    </div>

                    <div class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.objection') }}</span>
                        <span :class="{ 'grey--text': !visit.objections }">{{ visit.objections || $T('no-data') }}</span>
                    </div>

                    <div v-if="visit.purpose" class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.next-call') }}</span>
                        <span>{{ visit.purpose }}</span>
                    </div>

                    <div v-if="visit.unavailableMaterials?.length > 0" class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.unavailable-products') }}</span>
                        <span>{{ unavailableMaterials }}</span>
                    </div>

                    <div v-if="visit.durationInMinutes" class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.duration-minutes') }}</span>
                        <span>{{ $options.filters.formatNumber(visit.durationInMinutes, 3) }}</span>
                    </div>

                    <div v-if="visit.residentDoctorNames" class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.persons') }}</span>
                        <span>{{ visit.residentDoctorNames }}</span>
                    </div>

                    <div v-if="visit.competitiveSamples?.length > 0" class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.competitors') }}</span>
                        <span>{{ visit.competitiveSamples.join(', ') }}</span>
                    </div>

                    <div v-if="visit.otherSamples" class="visit-card__row">
                        <span class="visit-card__label">{{ $T('inputs.other-materials') }}</span>
                        <span>{{ visit.otherSamples }}</span>
                    </div>
                </v-card-text>

                <!-- top-up payment for a debit visit still owing a balance -->
                <div
                    v-if="!isPending && isSalesSystemEnabled && visit.payment === paymentMethod.debit && remainingAmountForDebit > 0"
                    class="px-3 pb-1"
                >
                    <v-btn text small color="primary" class="pa-0" @click="openAddPaymentDialog">
                        {{ $T('inputs.add-payment') }}
                    </v-btn>
                </div>

                <v-card-actions class="px-3 pb-3 pt-2">
                    <v-btn
                        depressed
                        color="grey lighten-4"
                        class="flex-grow-1 rounded-lg text-none grey--text text--darken-3"
                        @click="goToDetails"
                    >
                        <v-icon size="18" class="me-1">mdi-information-outline</v-icon>
                        {{ $T('Visits.visit-details') }}
                    </v-btn>

                    <!-- invoice button: shown only when usePrices (sales mode) is active -->
                    <v-btn
                        v-if="usePrices && !isPending"
                        depressed
                        color="orange lighten-5"
                        class="flex-grow-1 rounded-lg text-none orange--text text--darken-2"
                        @click="$emit('on-invoice', visit.id)"
                    >
                        <v-icon size="18" class="me-1">mdi-receipt-text-outline</v-icon>
                        {{ $T('Visits.view-invoice') }}
                    </v-btn>
                </v-card-actions>
            </v-card>

            <v-dialog
                v-model="orderDialog"
                max-width="560"
            >
                <v-card>
                    <v-toolbar flat color="primary" height="50" dark>
                        <v-spacer/>
                        {{ $T('Visits.order-details') }}
                        <v-spacer/>
                    </v-toolbar>

                    <v-card-text class="pt-4">
                        <v-progress-circular
                            v-if="loadingOrder"
                            indeterminate
                            color="primary"
                            class="d-block mx-auto"
                        />

                        <template v-else>
                            <div v-if="orderDetails?.length">
                                <v-row
                                    v-for="(item, index) in orderDetails"
                                    :key="index"
                                    dense
                                    class="py-1"
                                >
                                    <v-col cols="8">
                                        {{ getMaterialById(item.materialId) ? getFullMaterialName(getMaterialById(item.materialId)) : item.materialId }}
                                    </v-col>
                                    <v-col cols="4" class="text-end">
                                        {{ item.amount }}
                                    </v-col>
                                </v-row>
                            </div>
                            <div v-else class="text-center grey--text">
                                {{ $T('Visits.no-order-items') }}
                            </div>
                        </template>
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer/>
                        <v-btn text color="primary" @click="orderDialog = false">
                            {{ $T('close') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-dialog
                v-model="addPaymentDialog"
                max-width="560"
            >
                <v-card>
                    <v-toolbar flat color="primary" height="50" dark>
                        <v-spacer/>
                        {{$T('inputs.add-payment')}}
                        <v-spacer/>
                    </v-toolbar>

                    <v-card-text class="pt-4">
                        <v-sheet outlined rounded class="pa-3 payment-summary">
                            <div class="d-flex justify-space-between align-center mb-2">
                                <span class="text-body-2">{{$T('inputs.paid-amount')}}</span>
                                <span class="font-weight-bold">{{$options.filters.formatNumber(currentPaidAmount, 3)}}</span>
                            </div>
                            <div class="d-flex justify-space-between align-center mb-2">
                                <span class="text-body-2">{{$T('inputs.remaining-amount')}}</span>
                                <span class="font-weight-bold error--text">{{$options.filters.formatNumber(remainingAmountForDebit, 3)}}</span>
                            </div>
                            <div class="d-flex justify-space-between align-center">
                                <span class="text-body-2">{{$T('inputs.paid-amount-after-payment')}}</span>
                                <span class="font-weight-bold success--text">{{$options.filters.formatNumber(paidAmountAfterNewPayment, 3)}}</span>
                            </div>
                        </v-sheet>

                        <v-text-field
                            v-model.number="additionalPaymentAmount"
                            type="number"
                            dense
                            class="mt-3"
                            hide-spin-buttons
                            :label="$T('inputs.new-payment-amount')"
                            :rules="[
                                val => Number(val || 0) > 0 || $T('validation-messages.number'),
                                val => Number(val || 0) <= +remainingAmountForDebit || $T('messages.payment-amount-exceeds-remaining')
                            ]"
                        />
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer/>
                        <v-btn text color="success" :loading="paymentUpdateLoading" :disabled="paymentUpdateLoading" @click="applyAdditionalPayment">
                            {{$T('save')}}
                        </v-btn>
                        <v-btn text color="grey darken-1" :disabled="paymentUpdateLoading" @click="addPaymentDialog = false">
                            {{$T('close')}}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- swipe left: same action as the details button -->
            <v-fab-transition>
                <v-btn fab absolute right v-show="detailsFab" dark small text color="grey darken-3">
                    <v-icon>mdi-information-outline</v-icon>
                </v-btn>
            </v-fab-transition>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { useVisitOrderRequest } from '@/configs/general.configs'
import { paymentMethod } from '@/helpers/enums'
export default {
	props: {
		disabled: {
			type: Boolean,
			default: false
		},
		visit: {
			type: Object,
			required: true
		},
		usePrices: {
			type: Boolean,
			default: false
		},
		isPending: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			useVisitOrderRequest,
			paymentMethod,
			touchSwipeOffset: 0,
			maxOffsetAllowed: 30,
			isTouchDevice: true,

			snackbar: false,
			message: null,

			invoiceFab: false,
			detailsFab: false,
			orderDialog: false,
			visitOrder: null,
			loadingOrder: false,
			addPaymentDialog: false,
			additionalPaymentAmount: null,
			paymentUpdateLoading: false,
		}
	},

	methods: {
		// touch methods
		endOffset(e) {
			setTimeout(() => {
				if (this.touchSwipeOffset === this.maxOffsetAllowed) {
					if (this.usePrices && !this.isPending) this.$emit('on-invoice', this.visit.id);
				} else if (this.touchSwipeOffset === -this.maxOffsetAllowed) {
					this.goToDetails();
				}
				this.invoiceFab = false;
				this.detailsFab = false;
				this.touchSwipeOffset = 0;
			}, 200);
		},

		touchMove(e) {
			const offset = e.touchmoveX - e.touchstartX;
			this.touchSwipeOffset = offset > -this.maxOffsetAllowed && offset < this.maxOffsetAllowed ? offset : Math.sign(offset) * this.maxOffsetAllowed;

			if (this.touchSwipeOffset === this.maxOffsetAllowed) {
				this.invoiceFab = Boolean(this.usePrices) && !this.isPending;
				this.detailsFab = false;
			} else if (this.touchSwipeOffset === -this.maxOffsetAllowed) {
				this.invoiceFab = false;
				this.detailsFab = true;
			} else {
				this.invoiceFab = false;
				this.detailsFab = false;
			}
		},

		// component methods
		getFullMaterialName(material) {
			if (!material) return '';

			const details = [
				this.getMaterialFormById(material.materialFormId)?.name,
				material.dose,
				material.strength
			];

			return `${material.name} (${details.filter(c => c != null).join(' ')})`;
		},

		fetchAndShowOrder() {
			console.log('fetchAndShowOrder called with visit id:', this.visit.id, (this.useVisitOrderRequest === false || !this.visit.id));
			if (this.useVisitOrderRequest === false && !this.visit.id) return

			this.orderDialog = true
			this.loadingOrder = true
			this.visitOrder = null

			this.$store.dispatch('order/fetchOrderByVisitId', { visitId: this.visit.id })
				.then((response) => {
					this.visitOrder = response?.order || response || null
				})
				.catch(() => {
					this.visitOrder = null
				})
				.finally(() => {
					this.loadingOrder = false
				})
		},

		formatDeliveryDate(order) {
			const date = order?.orderDeliveryDate || order?.deliverDate
			return date ? window.moment(date).format('DD/MM/YYYY') : 'N/A'
		},

		openAddPaymentDialog() {
			this.additionalPaymentAmount = null
			this.addPaymentDialog = true
		},

		// a pending visit has no server id yet, so add-visit-details can't resolve it -
		// the sync-queue dialog is the only place with anything meaningful to show for it
		onCardClick() {
			if (this.isPending) {
				this.$eventBus.$emit('open-sync-queue')
				return
			}
			this.$emit('on-click')
		},

		goToDetails() {
			if (this.isPending) {
				this.$eventBus.$emit('open-sync-queue')
				return
			}
			this.$router.push({
				name: 'add-visit-details',
				query: { visitId: this.visit.id, readonly: 'true' }
			})
		},

		buildVisitUpdatePayload(newAmountReceived) {
			const unavailableMaterialIds = (this.visit.unavailableMaterials || [])
				.map(name => this.getMaterialByName(name)?.id)
				.filter(Boolean)

			return {
				id: this.visit.id,
				entityId: this.visit.entity?.id || this.visit.entityId,
				coreMessage: this.visit.coreMessage,
				durationInMinutes: this.visit.durationInMinutes,
				otherSamples: this.visit.otherSamples,
				notes: this.visit.notes,
				objections: this.visit.objections,
				nextVisitNotes: this.visit.nextVisitNotes,
				purpose: this.visit.purpose,
				materialAmounts: this.visit.materialAmounts || [],
				unavailableMaterialIds,
				competitiveSamples: this.visit.competitiveSamples || [],
				residentDoctorNames: this.visit.residentDoctorNames || null,
				payment: this.visit.payment,
				amountReceived: newAmountReceived,
				createdAt: this.visit.createdAt,
			}
		},

		applyAdditionalPayment() {
			const paymentToAdd = Number(this.additionalPaymentAmount || 0)
			if (!paymentToAdd) return

			if (paymentToAdd > this.remainingAmountForDebit) {
				this.$eventBus.$emit('show-snackbar', this.$T('messages.payment-amount-exceeds-remaining'), 'info')
				return
			}

			const newAmountReceived = this.currentPaidAmount + paymentToAdd
			const payload = this.buildVisitUpdatePayload(newAmountReceived)

			this.paymentUpdateLoading = true
			this.$store.dispatch('visits/update', payload)
				.then(() => {
					this.$eventBus.$emit('show-snackbar', this.$T('Visits.visit-is-updated'), 'success')
					this.$store.dispatch('profile/fetchBalance')
					this.$emit('on-payment-updated')
					this.addPaymentDialog = false
					this.additionalPaymentAmount = null
				})
				.finally(() => {
					this.paymentUpdateLoading = false
				})
		},
	},

	computed: {
		...mapState({
			salesSystem: state => state.configurations.systemConfigValue.properties?.find(c => c.key === 'salesSystem')?.value,
		}),

		isSalesSystemEnabled() {
			return this.salesSystem === true
		},

		visitTotal() {
			return (this.visit.materialAmounts || []).reduce((sum, item) => {
				const amount = Number(item?.amount || 0)
				const unitPrice = Number(item?.sellPrice || item?.price || 0)
				const discount = Number(item?.priceDiscount || item?.discount || 0)
				const gross = amount * unitPrice
				return sum + (gross - (discount / 100) * gross)
			}, 0)
		},

		currentPaidAmount() {
			return Number(this.visit.amountReceived || 0)
		},

		remainingAmountForDebit() {
			const remaining = this.visitTotal - this.currentPaidAmount
			return remaining > 0 ? remaining : 0
		},

		paidAmountAfterNewPayment() {
			return this.currentPaidAmount + Number(this.additionalPaymentAmount || 0)
		},

		orderDetails() {
			if (!this.visitOrder) return []

			const details = Array.isArray(this.visitOrder.orderDetail)
				? this.visitOrder.orderDetail
				: (Array.isArray(this.visitOrder.orderDetails) ? this.visitOrder.orderDetails : [])

			return details.filter(item => item && item.materialId && item.amount)
		},

		unavailableMaterials() {
			const materials = [];
			this.visit.unavailableMaterials.forEach(materialName => {
				const material = this.getMaterialByName(materialName);
				if (!material) return;
				materials.push(this.getFullMaterialName(material));
			});
			return materials.join(', ');
		},

		customerTypeName() {
			return this.getCustomerTypeById(this.visit.entity?.entityTypeId)?.name || null
		},

		formattedDate() {
			return window.moment(this.visit.createdAt).format('DD MMMM YYYY')
		},

		...mapGetters({
			getMaterialById: 'materials/getMaterialById',
			getMaterialByName: 'materials/getMaterialByName',
			getMaterialFormById: 'materialForms/getMaterialFormById',
			getCustomerTypeById: 'entities/getCustomerTypeById'
		})
	},

	mounted() {
		this.isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
	}
}
</script>

<style lang="scss">
@import '../scss/vars.scss';
.payment-summary {
	background: linear-gradient(180deg, #fafcfc 0%, #ffffff 100%);
	border-color: #d8e6e6 !important;
}

.visit-card{
    transition: transform .4s ease;
    overflow: hidden;
}
.visit-ripple{
    color: rgba($primary, .5);
}
.visit-card__row{
    display: flex;
    align-items: flex-start;
    gap: .75rem;
    margin-bottom: .25rem;
    font-size: .875rem;
}
.visit-card__label{
    flex: 0 0 auto;
    min-width: 5rem;
    color: rgba(0, 0, 0, .6);
}
</style>
